import React, { useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import DynamicModal from "../../Modal/DynamicModal";
import { addLayer } from "../../../utils/addingLayers/AddVoltagesLayers";
import { BASE_URL } from "../../../config";
import { MdCancel, MdEdit, MdDeleteOutline } from "react-icons/md";
import { TbFilterPlus } from "react-icons/tb";
import { toast } from "react-toastify";
import axios from "axios";
import { getAccessToken } from "../../../utils/LoginForm/Auth/auth";
import "./LayerFilterPanel.css";

const OPS = [
    { k: "=", label: "=" },
    { k: "!=", label: "≠" },
    { k: ">", label: ">" },
    { k: ">=", label: "≥" },
    { k: "<", label: "<" },
    { k: "<=", label: "≤" },
    { k: "ILIKE", label: "ILIKE (sadrži)" },
    { k: "LIKE", label: "LIKE" },
    { k: "BETWEEN", label: "BETWEEN" },
    { k: "IN", label: "IN (v1,v2,…)" },
    { k: "IS NULL", label: "IS NULL" },
    { k: "IS NOT NULL", label: "IS NOT NULL" },
];

const NUMERIC_OP_KEYS = [
    "=",
    "!=",
    ">",
    ">=",
    "<",
    "<=",
    "BETWEEN",
    "IN",
    "IS NULL",
    "IS NOT NULL",
];

const STRING_OP_KEYS = [
    "=",
    "!=",
    "ILIKE",
    "LIKE",
    "IN",
    "IS NULL",
    "IS NOT NULL",
];

const DATE_OP_KEYS = [
    "=",
    "!=",
    ">",
    ">=",
    "<",
    "<=",
    "BETWEEN",
    "IS NULL",
    "IS NOT NULL",
];

const BOOLEAN_OP_KEYS = ["=", "!=", "IS NULL", "IS NOT NULL"];

const KNOWN_LAYER_NAMES = [
    "stubovi",
    "trafo_stanice",
    "mjerna_mjesta",
    "vod",
    "dionica",
    "ocitacki_hod",
];

const token = getAccessToken();

const makeRule = () => ({
    id: `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
    column: "",
    operator: "",
    value: "",
    value2: "",
    joinWithPrev: "AND",
});

const fetchColumnsFromGeoServer = async (layerName) => {
    const describeUrl = `${BASE_URL}/geoserver/wfs?service=WFS&version=1.1.0&request=DescribeFeatureType&typeName=${layerName}`;

    const response = await axios.get(describeUrl, {
        headers: {
            Accept: "application/xml",
            Authorization: `Bearer ${token}`,
        },
    });

    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(response.data, "application/xml");
    const elements = Array.from(xmlDoc.getElementsByTagName("xsd:element"));

    return elements
        .map((el) => {
            const name = el.getAttribute("name");
            const typeAttr = (el.getAttribute("type") || "").toLowerCase();

            if (!name) return null;
            if (["geometrija", "geom", "the_geom"].includes(name)) return null;

            let type = "string";

            if (
                typeAttr.includes("int") ||
                typeAttr.includes("long") ||
                typeAttr.includes("short") ||
                typeAttr.includes("bigint") ||
                typeAttr.includes("integer")
            ) {
                type = "integer";
            } else if (
                typeAttr.includes("double") ||
                typeAttr.includes("float") ||
                typeAttr.includes("decimal") ||
                typeAttr.includes("number")
            ) {
                type = "float";
            } else if (typeAttr.includes("boolean")) {
                type = "boolean";
            } else if (typeAttr.includes("date") || typeAttr.includes("time")) {
                type = "date";
            }

            return { name, type };
        })
        .filter(Boolean);
};

const isNumeric = (type = "") => ["integer", "float"].includes(String(type).toLowerCase());
const isDateLike = (type = "") => String(type).toLowerCase() === "date";
const isBoolean = (type = "") => String(type).toLowerCase() === "boolean";

const getOperatorsForType = (type = "") => {
    if (isNumeric(type)) return NUMERIC_OP_KEYS;
    if (isDateLike(type)) return DATE_OP_KEYS;
    if (isBoolean(type)) return BOOLEAN_OP_KEYS;
    return STRING_OP_KEYS;
};

const quoteValue = (val, type) => {
    if (val == null || String(val).trim() === "") return "''";

    if (isNumeric(type)) return String(val).trim();

    if (isBoolean(type)) {
        const normalized = String(val).toLowerCase().trim();
        return normalized === "true" ? "true" : "false";
    }

    const escaped = String(val).replaceAll("'", "''");
    return `'${escaped}'`;
};

const buildRuleCql = ({ column, operator, value, value2, type }) => {
    if (!column || !operator) return "";

    if (operator === "IS NULL" || operator === "IS NOT NULL") {
        return `${column} ${operator}`;
    }

    if (operator === "BETWEEN") {
        if (value === "" || value2 === "") return "";
        return `${column} BETWEEN ${quoteValue(value, type)} AND ${quoteValue(value2, type)}`;
    }

    if (operator === "IN") {
        const parts = String(value || "")
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);

        if (!parts.length) return "";

        const list = parts.map((v) => quoteValue(v, type)).join(", ");
        return `${column} IN (${list})`;
    }

    if (operator === "ILIKE") {
        if (value === "") return "";
        const escaped = String(value).replaceAll("'", "''");
        return `${column} ILIKE '%${escaped}%'`;
    }

    if (operator === "LIKE") {
        if (value === "") return "";
        const escaped = String(value).replaceAll("'", "''");
        return `${column} LIKE '%${escaped}%'`;
    }

    if (value === "") return "";

    const normalizedOperator = operator === "!=" ? "<>" : operator;
    return `${column} ${normalizedOperator} ${quoteValue(value, type)}`;
};

const normalizeArray = (value) => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
};

const getLayerId = (layer) => String(layer?.id || "");

const uniqueById = (layers = []) => {
    const seen = new Set();

    return layers.filter((layer) => {
        const id = getLayerId(layer);

        if (!id || seen.has(id)) return false;

        seen.add(id);
        return true;
    });
};

const getBaseLayerName = (layerOrId) => {
    if (!layerOrId) return "";

    if (typeof layerOrId === "object") {
        const explicitOriginal =
            layerOrId?.props?.originalLayerName || layerOrId?.originalLayerName;

        if (explicitOriginal) return explicitOriginal;
    }

    let raw = typeof layerOrId === "string" ? layerOrId : layerOrId?.id || "";

    raw = String(raw)
        .replace(/__filtered__wfs__\d+$/, "")
        .replace(/__filtered__wms__\d+$/, "");

    if (raw.endsWith("-layer")) {
        raw = raw.slice(0, -6);
    }

    const matched = KNOWN_LAYER_NAMES.find(
        (name) => raw === name || raw.startsWith(`${name}-`)
    );

    return matched || raw;
};

const isSameBaseLayer = (layer, layerName) => getBaseLayerName(layer) === layerName;

const makeFilteredId = (layer, type, index) => {
    const baseId = layer?.id || `${type}-${index}`;
    return `${baseId}__filtered__${type}__${index}`;
};

const cloneLayerWithId = (layer, newId, extraProps = {}) => {
    const mergedProps = {
        ...(layer?.props || {}),
        ...extraProps,
    };

    if (layer && typeof layer.clone === "function") {
        return layer.clone({
            id: newId,
            visible: true,
            ...mergedProps,
        });
    }

    return {
        ...layer,
        id: newId,
        visible: true,
        ...extraProps,
        props: mergedProps,
    };
};

const recreateLayerInstance = (layer) => {
    if (!layer) return null;

    const cleanProps = {
        ...(layer.props || {}),
    };

    delete cleanProps.isFiltered;
    delete cleanProps.originalLayerName;
    delete cleanProps.appliedCqlFilter;
    delete cleanProps.displayAppliedFilters;

    if (typeof layer.clone === "function") {
        return layer.clone({
            id: layer.id,
            visible: layer.props?.visible ?? true,
            ...cleanProps,
        });
    }

    return {
        ...layer,
        props: cleanProps,
    };
};

const captureSetLayers = () => {
    let captured = [];

    const setter = (valueOrUpdater) => {
        if (typeof valueOrUpdater === "function") {
            captured = valueOrUpdater(captured);
        } else if (Array.isArray(valueOrUpdater)) {
            captured = valueOrUpdater;
        } else if (valueOrUpdater) {
            captured = [valueOrUpdater];
        } else {
            captured = [];
        }

        return captured;
    };

    const getCaptured = () => normalizeArray(captured);

    return { setter, getCaptured };
};

const extractColumnsFromRules = (rules = []) =>
    rules.map((r) => r.column).filter(Boolean);

const splitTopLevelAnd = (cql = "") => {
    if (!cql || !String(cql).trim()) return [];

    const result = [];
    let depth = 0;
    let current = "";
    let i = 0;
    const text = String(cql).trim();

    while (i < text.length) {
        const char = text[i];

        if (char === "(") {
            depth += 1;
            current += char;
            i += 1;
            continue;
        }

        if (char === ")") {
            depth = Math.max(0, depth - 1);
            current += char;
            i += 1;
            continue;
        }

        if (depth === 0 && text.slice(i, i + 5).toUpperCase() === " AND ") {
            if (current.trim()) result.push(current.trim());
            current = "";
            i += 5;
            continue;
        }

        current += char;
        i += 1;
    }

    if (current.trim()) result.push(current.trim());

    return result;
};

const stripOuterParens = (str = "") => {
    let s = String(str).trim();

    while (s.startsWith("(") && s.endsWith(")")) {
        let depth = 0;
        let valid = true;

        for (let i = 0; i < s.length; i += 1) {
            if (s[i] === "(") depth += 1;
            if (s[i] === ")") depth -= 1;

            if (depth === 0 && i < s.length - 1) {
                valid = false;
                break;
            }
        }

        if (!valid) break;

        s = s.slice(1, -1).trim();
    }

    return s;
};

const getConditionColumn = (condition = "") => {
    const cleaned = stripOuterParens(condition);
    const match = cleaned.match(
        /^("?[\w\d_]+"?)\s+(=|<>|!=|>|>=|<|<=|ILIKE|LIKE|IN|BETWEEN|IS NULL|IS NOT NULL)\b/i
    );

    if (!match) return null;

    return match[1].replaceAll('"', "");
};

const mergeBaseAndUserCql = (baseCqlRaw = "", userCql = "", rules = []) => {
    const userColumns = extractColumnsFromRules(rules);

    if (!baseCqlRaw && !userCql) return "";
    if (!baseCqlRaw) return userCql;
    if (!userCql) return baseCqlRaw;

    const baseConditions = splitTopLevelAnd(baseCqlRaw);
    const filteredBaseConditions = baseConditions.filter((condition) => {
        const col = getConditionColumn(condition);

        if (!col) return true;

        return !userColumns.includes(col);
    });

    const cleanedBaseCql = filteredBaseConditions.join(" AND ").trim();

    if (cleanedBaseCql && userCql) {
        return `(${cleanedBaseCql}) AND (${userCql})`;
    }

    return cleanedBaseCql || userCql;
};

const formatRuleLabel = (rule) => {
    if (!rule?.column || !rule?.operator) return "";

    if (rule.operator === "IS NULL" || rule.operator === "IS NOT NULL") {
        return `${rule.column} ${rule.operator}`;
    }

    if (rule.operator === "BETWEEN") {
        return `${rule.column} BETWEEN ${rule.value || ""} AND ${rule.value2 || ""}`;
    }

    if (rule.operator === "IN") {
        return `${rule.column} IN (${rule.value || ""})`;
    }

    if (rule.operator === "ILIKE" || rule.operator === "LIKE") {
        return `${rule.column} ${rule.operator} ${rule.value || ""}`;
    }

    return `${rule.column} ${rule.operator} ${rule.value || ""}`;
};

const buildDisplayFilters = ({ baseCql = "", rules = [], finalCql = "" }) => {
    const items = [];

    if (baseCql) {
        splitTopLevelAnd(baseCql).forEach((part) => {
            const cleaned = stripOuterParens(part);

            if (cleaned) {
                items.push({
                    id: `base_${cleaned}`,
                    label: cleaned,
                    source: "base",
                });
            }
        });
    }

    if (rules?.length) {
        rules.forEach((rule) => {
            const label = formatRuleLabel(rule);

            if (label) {
                items.push({
                    id: rule.id,
                    label,
                    source: "user",
                });
            }
        });
    }

    if (!items.length && finalCql) {
        items.push({
            id: `fallback_${finalCql}`,
            label: finalCql,
            source: "fallback",
        });
    }

    return items;
};

const LayerFilterPanel = ({
    wfsLayers,
    wmsLayers,
    setHoverInfo,
    handleClickInfo,
    setWfsLayers,
    setWmsLayers,
    viewState,
    setViewState,
    setOverlay,
    id_terenske_jedinice,
    activeFilters,
    setActiveFilters,
}) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedLayer, setSelectedLayer] = useState(null);
    const [rules, setRules] = useState([makeRule()]);
    const [columns, setColumns] = useState([]);
    const [loadingColumns, setLoadingColumns] = useState(false);
    const [editingRuleId, setEditingRuleId] = useState(null);

    const selectedLayerName = useMemo(() => {
        return selectedLayer ? getBaseLayerName(selectedLayer) : "";
    }, [selectedLayer]);

    const getColumnType = (columnName) => {
        return columns.find((c) => c.name === columnName)?.type || "string";
    };

    const openFilterModal = async (layer, ruleIdToEdit = null) => {
        const layerName = getBaseLayerName(layer);
        const saved = activeFilters?.[layerName];

        setSelectedLayer(saved?.displayLayer || layer);
        setModalOpen(true);
        setLoadingColumns(true);
        setColumns([]);
        setEditingRuleId(ruleIdToEdit);

        const initialRules = saved?.rules?.length
            ? saved.rules.map((rule) => ({ ...rule }))
            : [makeRule()];

        setRules(initialRules);

        try {
            const detectedColumns = await fetchColumnsFromGeoServer(layerName);
            setColumns(detectedColumns);

            if (saved?.rules?.length) {
                const normalized = saved.rules.map((rule) => {
                    const type = detectedColumns.find((c) => c.name === rule.column)?.type;
                    const allowed = getOperatorsForType(type);

                    return {
                        ...rule,
                        operator: allowed.includes(rule.operator)
                            ? rule.operator
                            : allowed[0] || "=",
                    };
                });

                setRules(normalized);
            }
        } catch (err) {
            console.error("Greška pri učitavanju kolona:", err);
            toast.error("Ne mogu učitati kolone za izabrani sloj.");
            setColumns([]);
            setRules(saved?.rules?.length ? saved.rules.map((r) => ({ ...r })) : [makeRule()]);
        } finally {
            setLoadingColumns(false);
        }
    };

    const closeFilterModal = () => {
        setModalOpen(false);
        setSelectedLayer(null);
        setRules([makeRule()]);
        setColumns([]);
        setLoadingColumns(false);
        setEditingRuleId(null);
    };

    const updateRule = (ruleId, patch) => {
        setRules((prev) =>
            prev.map((rule) => {
                if (rule.id !== ruleId) return rule;

                const updated = { ...rule, ...patch };

                if (Object.prototype.hasOwnProperty.call(patch, "column")) {
                    const nextType = getColumnType(patch.column);
                    const allowedOps = getOperatorsForType(nextType);

                    updated.operator = allowedOps.includes(updated.operator)
                        ? updated.operator
                        : allowedOps[0] || "=";

                    updated.value = "";
                    updated.value2 = "";
                }

                if (Object.prototype.hasOwnProperty.call(patch, "operator")) {
                    if (patch.operator === "IS NULL" || patch.operator === "IS NOT NULL") {
                        updated.value = "";
                        updated.value2 = "";
                    }

                    if (patch.operator !== "BETWEEN") {
                        updated.value2 = "";
                    }
                }

                return updated;
            })
        );
    };

    const addRule = () => {
        const newRule = { ...makeRule(), joinWithPrev: "AND" };

        setRules((prev) => [...prev, newRule]);
        setEditingRuleId(newRule.id);
    };

    const removeRule = (ruleId) => {
        setRules((prev) => {
            const next = prev.filter((rule) => rule.id !== ruleId);
            const normalized = next.length ? next : [makeRule()];

            if (editingRuleId === ruleId) {
                setEditingRuleId(normalized[0]?.id || null);
            }

            return normalized;
        });
    };

    const loadFilteredPairForLayer = async (layerName, cqlFilter) => {
        const wmsCollector = captureSetLayers();

        const generatedWfs = await addLayer(
            layerName,
            setHoverInfo,
            handleClickInfo,
            null,
            wmsCollector.setter,
            null,
            viewState,
            setViewState,
            false,
            setOverlay,
            id_terenske_jedinice,
            cqlFilter,
            false
        );

        const normalizedWfs = normalizeArray(generatedWfs).map((layer, index) =>
            cloneLayerWithId(layer, makeFilteredId(layer, "wfs", index), {
                isFiltered: true,
                originalLayerName: layerName,
                appliedCqlFilter: cqlFilter,
            })
        );

        const normalizedWms = normalizeArray(wmsCollector.getCaptured()).map((layer, index) =>
            cloneLayerWithId(layer, makeFilteredId(layer, "wms", index), {
                isFiltered: true,
                originalLayerName: layerName,
                appliedCqlFilter: cqlFilter,
            })
        );

        return {
            filteredWfs: normalizedWfs,
            filteredWms: normalizedWms,
        };
    };

    const resetLayer = (layerName) => {
        const entry = activeFilters?.[layerName];

        if (!entry) return;

        const targetLayerNames = Object.keys(entry.targets || {});
        const restoreWfs = [];
        const restoreWms = [];
        const filteredWfsIds = [];
        const filteredWmsIds = [];

        targetLayerNames.forEach((targetName) => {
            const targetEntry = entry.targets[targetName];

            restoreWfs.push(
                ...normalizeArray(targetEntry.originalWfs)
                    .map(recreateLayerInstance)
                    .filter(Boolean)
            );

            restoreWms.push(
                ...normalizeArray(targetEntry.originalWms)
                    .map(recreateLayerInstance)
                    .filter(Boolean)
            );

            filteredWfsIds.push(...normalizeArray(targetEntry.filteredWfsIds));
            filteredWmsIds.push(...normalizeArray(targetEntry.filteredWmsIds));
        });

        const uniqueRestoreWfs = uniqueById(restoreWfs);
        const uniqueRestoreWms = uniqueById(restoreWms);

        setWfsLayers((prev) => {
            const next = prev.filter((layer) => !filteredWfsIds.includes(layer.id));
            return uniqueById([...next, ...uniqueRestoreWfs]);
        });

        setWmsLayers((prev) => {
            const next = prev.filter((layer) => !filteredWmsIds.includes(layer.id));
            return uniqueById([...next, ...uniqueRestoreWms]);
        });

        setActiveFilters((prev) => {
            const updated = { ...(prev || {}) };
            delete updated[layerName];
            return updated;
        });

        toast.success("Filter je uklonjen.");
    };

    const deleteRuleFromLayerCard = async (layerName, ruleId) => {
        const entry = activeFilters?.[layerName];

        if (!entry) return;

        const nextRules = (entry.rules || []).filter((r) => r.id !== ruleId);

        if (!nextRules.length) {
            resetLayer(layerName);
            return;
        }

        const displayLayer = entry.displayLayer;

        if (!displayLayer) return;

        try {
            const originalBaseCql =
                entry?.baseCql ||
                displayLayer?.props?.originalCqlFilter ||
                displayLayer?.originalCqlFilter ||
                "";

            const expressions = [];

            for (let i = 0; i < nextRules.length; i += 1) {
                const rule = nextRules[i];
                const colTypeGuess = columns.find((c) => c.name === rule.column)?.type || "string";

                const cql = buildRuleCql({
                    column: rule.column,
                    operator: rule.operator,
                    value: rule.value,
                    value2: rule.value2,
                    type: colTypeGuess,
                });

                if (!cql) continue;

                if (expressions.length === 0) {
                    expressions.push(cql);
                } else {
                    expressions.push(`${rule.joinWithPrev || "AND"} ${cql}`);
                }
            }

            const userCql = expressions.join(" ").trim();

            if (!userCql) {
                resetLayer(layerName);
                return;
            }

            const finalCql = mergeBaseAndUserCql(originalBaseCql, userCql, nextRules);
            const displayAppliedFilters = buildDisplayFilters({
                baseCql: originalBaseCql,
                rules: nextRules,
                finalCql,
            });

            const targetLayerNames = layerName === "vod" ? ["vod", "dionica"] : [layerName];
            const nextTargets = {};
            const allFilteredWfs = [];
            const allFilteredWms = [];

            for (const targetName of targetLayerNames) {
                const savedTarget = entry?.targets?.[targetName];

                const originalWfs = savedTarget?.originalWfs?.length
                    ? savedTarget.originalWfs
                    : wfsLayers.filter((layer) => isSameBaseLayer(layer, targetName));

                const originalWms = savedTarget?.originalWms?.length
                    ? savedTarget.originalWms
                    : wmsLayers.filter((layer) => isSameBaseLayer(layer, targetName));

                const { filteredWfs, filteredWms } = await loadFilteredPairForLayer(
                    targetName,
                    finalCql
                );

                const enrichedFilteredWfs = filteredWfs.map((layer) =>
                    cloneLayerWithId(layer, layer.id, {
                        originalLayerName: targetName,
                        appliedCqlFilter: finalCql,
                        displayAppliedFilters,
                    })
                );

                const enrichedFilteredWms = filteredWms.map((layer) =>
                    cloneLayerWithId(layer, layer.id, {
                        originalLayerName: targetName,
                        appliedCqlFilter: finalCql,
                        displayAppliedFilters,
                    })
                );

                nextTargets[targetName] = {
                    originalWfs: uniqueById(originalWfs),
                    originalWms: uniqueById(originalWms),
                    filteredWfsIds: enrichedFilteredWfs.map((layer) => layer.id),
                    filteredWmsIds: enrichedFilteredWms.map((layer) => layer.id),
                };

                allFilteredWfs.push(...enrichedFilteredWfs);
                allFilteredWms.push(...enrichedFilteredWms);
            }

            const originalWfsIdsToRemove = Object.values(nextTargets)
                .flatMap((t) => t.originalWfs || [])
                .map((layer) => layer.id);

            const originalWmsIdsToRemove = Object.values(nextTargets)
                .flatMap((t) => t.originalWms || [])
                .map((layer) => layer.id);

            const previousFilteredWfsIds = Object.values(entry?.targets || {}).flatMap(
                (t) => t.filteredWfsIds || []
            );

            const previousFilteredWmsIds = Object.values(entry?.targets || {}).flatMap(
                (t) => t.filteredWmsIds || []
            );

            setWfsLayers((prev) => {
                const next = prev.filter(
                    (layer) =>
                        !originalWfsIdsToRemove.includes(layer.id) &&
                        !previousFilteredWfsIds.includes(layer.id)
                );

                return [...next, ...allFilteredWfs];
            });

            setWmsLayers((prev) => {
                const next = prev.filter(
                    (layer) =>
                        !originalWmsIdsToRemove.includes(layer.id) &&
                        !previousFilteredWmsIds.includes(layer.id)
                );

                return [...next, ...allFilteredWms];
            });

            setActiveFilters((prev) => ({
                ...(prev || {}),
                [layerName]: {
                    ...entry,
                    userCql,
                    finalCql,
                    displayAppliedFilters,
                    rules: nextRules.map((rule) => ({ ...rule })),
                    targets: nextTargets,
                },
            }));

            toast.success("Filter je obrisan i sloj je osvježen.");
        } catch (err) {
            console.error("Greška pri brisanju filter pravila:", err);
            toast.error("Brisanje filtera nije uspjelo.");
        }
    };

    const buildFullCql = () => {
        const expressions = [];

        for (let i = 0; i < rules.length; i += 1) {
            const rule = rules[i];
            const type = getColumnType(rule.column);

            const cql = buildRuleCql({
                column: rule.column,
                operator: rule.operator,
                value: rule.value,
                value2: rule.value2,
                type,
            });

            if (!cql) continue;

            if (expressions.length === 0) {
                expressions.push(cql);
            } else {
                expressions.push(`${rule.joinWithPrev || "AND"} ${cql}`);
            }
        }

        return expressions.join(" ");
    };

    const applyFilter = async () => {
        if (!selectedLayer) {
            toast.error("Nije izabran sloj.");
            return;
        }

        const userCql = buildFullCql();

        if (!userCql.trim()) {
            toast.warn("Nema validnih uslova za filter.");
            return;
        }

        const selectedBaseLayer = getBaseLayerName(selectedLayer);
        const targetLayerNames = selectedBaseLayer === "vod" ? ["vod", "dionica"] : [selectedBaseLayer];

        try {
            const existingEntry = activeFilters?.[selectedBaseLayer];
            const nextTargets = {};
            const allFilteredWfs = [];
            const allFilteredWms = [];

            const originalBaseCql =
                existingEntry?.baseCql ||
                selectedLayer?.props?.originalCqlFilter ||
                selectedLayer?.originalCqlFilter ||
                "";

            const finalCql = mergeBaseAndUserCql(originalBaseCql, userCql, rules);
            const displayAppliedFilters = buildDisplayFilters({
                baseCql: originalBaseCql,
                rules,
                finalCql,
            });

            for (const targetName of targetLayerNames) {
                const savedTarget = existingEntry?.targets?.[targetName];

                const originalWfs = savedTarget?.originalWfs?.length
                    ? savedTarget.originalWfs
                    : wfsLayers.filter((layer) => isSameBaseLayer(layer, targetName));

                const originalWms = savedTarget?.originalWms?.length
                    ? savedTarget.originalWms
                    : wmsLayers.filter((layer) => isSameBaseLayer(layer, targetName));

                const { filteredWfs, filteredWms } = await loadFilteredPairForLayer(
                    targetName,
                    finalCql
                );

                const enrichedFilteredWfs = filteredWfs.map((layer) =>
                    cloneLayerWithId(layer, layer.id, {
                        originalLayerName: targetName,
                        appliedCqlFilter: finalCql,
                        displayAppliedFilters,
                    })
                );

                const enrichedFilteredWms = filteredWms.map((layer) =>
                    cloneLayerWithId(layer, layer.id, {
                        originalLayerName: targetName,
                        appliedCqlFilter: finalCql,
                        displayAppliedFilters,
                    })
                );

                nextTargets[targetName] = {
                    originalWfs: uniqueById(originalWfs),
                    originalWms: uniqueById(originalWms),
                    filteredWfsIds: enrichedFilteredWfs.map((layer) => layer.id),
                    filteredWmsIds: enrichedFilteredWms.map((layer) => layer.id),
                };

                allFilteredWfs.push(...enrichedFilteredWfs);
                allFilteredWms.push(...enrichedFilteredWms);
            }

            if (!allFilteredWfs.length && !allFilteredWms.length) {
                toast.error("Filtriranje nije vratilo ni WFS ni WMS sloj.");
                return;
            }

            const originalWfsIdsToRemove = Object.values(nextTargets)
                .flatMap((t) => t.originalWfs || [])
                .map((layer) => layer.id);

            const originalWmsIdsToRemove = Object.values(nextTargets)
                .flatMap((t) => t.originalWms || [])
                .map((layer) => layer.id);

            const previousFilteredWfsIds = Object.values(existingEntry?.targets || {}).flatMap(
                (t) => t.filteredWfsIds || []
            );

            const previousFilteredWmsIds = Object.values(existingEntry?.targets || {}).flatMap(
                (t) => t.filteredWmsIds || []
            );

            setWfsLayers((prev) => {
                const next = prev.filter(
                    (layer) =>
                        !originalWfsIdsToRemove.includes(layer.id) &&
                        !previousFilteredWfsIds.includes(layer.id)
                );

                return [...next, ...allFilteredWfs];
            });

            setWmsLayers((prev) => {
                const next = prev.filter(
                    (layer) =>
                        !originalWmsIdsToRemove.includes(layer.id) &&
                        !previousFilteredWmsIds.includes(layer.id)
                );

                return [...next, ...allFilteredWms];
            });

            setActiveFilters((prev) => ({
                ...(prev || {}),
                [selectedBaseLayer]: {
                    displayLayer: existingEntry?.displayLayer || selectedLayer,
                    baseCql: originalBaseCql,
                    userCql,
                    finalCql,
                    displayAppliedFilters,
                    rules: rules.map((rule) => ({ ...rule })),
                    targets: nextTargets,
                },
            }));

            closeFilterModal();
        } catch (err) {
            console.error("Greška pri primjeni filtera:", err);
            toast.error("Primjena filtera nije uspjela.");
        }
    };

    const previewCql = useMemo(() => {
        const layerName = selectedLayer ? getBaseLayerName(selectedLayer) : "";
        const saved = layerName ? activeFilters?.[layerName] : null;

        const baseCql =
            saved?.baseCql ||
            selectedLayer?.props?.originalCqlFilter ||
            selectedLayer?.originalCqlFilter ||
            "";

        const userCql = buildFullCql();

        return mergeBaseAndUserCql(baseCql, userCql, rules);
    }, [rules, columns, selectedLayer, activeFilters]);

    const listedLayers = useMemo(() => {
        const activeOriginalLayers = wfsLayers.filter(
            (layer) =>
                layer?.props?.source === "internal" &&
                !String(layer?.id || "").includes("__filtered__") &&
                getBaseLayerName(layer) !== "dionica"
        );

        const preservedFromFilters = Object.entries(activeFilters || {})
            .map(([layerName, entry]) => {
                if (layerName === "dionica") return null;
                return entry?.displayLayer || null;
            })
            .filter(Boolean);

        const merged = uniqueById([...activeOriginalLayers, ...preservedFromFilters]);
        const seenBase = new Set();

        return merged.filter((layer) => {
            const base = getBaseLayerName(layer);

            if (!base || seenBase.has(base)) return false;

            seenBase.add(base);
            return true;
        });
    }, [wfsLayers, activeFilters]);

    return (
        <div className="filter-panel">
            <div className="filter-panel-header">
                <div>
                    <div className="filter-panel-title">Slojevi</div>
                    <div className="filter-panel-subtitle">
                        Upravljanje aktivnim filterima po slojevima
                    </div>
                </div>
            </div>

            <div className="filter-list">
                {listedLayers.map((layer, index) => {
                    const layerName = getBaseLayerName(layer);
                    const filterEntry = activeFilters?.[layerName];
                    const isFiltered = !!filterEntry;

                    const baseFilterFromLayer =
                        layer?.props?.originalCqlFilter ||
                        layer?.originalCqlFilter ||
                        "";

                    const displayFilters = filterEntry?.displayAppliedFilters?.length
                        ? filterEntry.displayAppliedFilters
                        : baseFilterFromLayer
                            ? splitTopLevelAnd(baseFilterFromLayer).map((item) => ({
                                id: `base_${item}`,
                                label: stripOuterParens(item),
                                source: "base",
                            }))
                            : [];

                    return (
                        <div key={`${layer.id}_${index}`} className="layer-filter-card">
                            <div className="layer-filter-card-top">
                                <div className="layer-filter-main">
                                    <div className="layer-filter-heading">
                                        <div className="layer-name">{layerName}</div>

                                        <div className="layer-filter-meta">
                                            {displayFilters.length > 0
                                                ? `${displayFilters.length} aktivna filtera`
                                                : "Nema aktivnih filtera"}
                                        </div>
                                    </div>
                                </div>

                                <div className="layer-header-actions">
                                    <button
                                        type="button"
                                        className="layer-action-btn primary"
                                        onClick={() => openFilterModal(layer)}
                                        title="Dodaj ili izmijeni filtere"
                                    >
                                        <TbFilterPlus size={17} />
                                        <span>Filter</span>
                                    </button>

                                    <button
                                        type="button"
                                        className="layer-action-btn danger"
                                        title="Poništi sve filtere"
                                        disabled={!isFiltered}
                                        onClick={() => resetLayer(layerName)}
                                    >
                                        <MdCancel size={17} />
                                        <span>Poništi</span>
                                    </button>
                                </div>
                            </div>

                            {displayFilters.length > 0 && (
                                <div className="layer-filters-stack">
                                    {displayFilters.map((item) => {
                                        const isUserRule = item.source === "user";

                                        return (
                                            <div
                                                key={`${layerName}_${item.id}`}
                                                className="layer-filter-row"
                                            >
                                                <div className="layer-filter-row-left">
                                                    <span className="layer-filter-text">
                                                        {item.label}
                                                    </span>
                                                </div>

                                                <div className="layer-filter-row-actions">
                                                    {isUserRule && (
                                                        <>
                                                            <button
                                                                type="button"
                                                                className="icon-action-btn"
                                                                title="Uredi filter"
                                                                onClick={() =>
                                                                    openFilterModal(layer, item.id)
                                                                }
                                                            >
                                                                <MdEdit size={17} />
                                                            </button>

                                                            <button
                                                                type="button"
                                                                className="icon-action-btn delete"
                                                                title="Obriši filter"
                                                                onClick={() =>
                                                                    deleteRuleFromLayerCard(
                                                                        layerName,
                                                                        item.id
                                                                    )
                                                                }
                                                            >
                                                                <MdDeleteOutline size={18} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            <DynamicModal
                isOpen={modalOpen}
                onRequestClose={closeFilterModal}
                title={`Filter za ${selectedLayerName}`}
                customWidth="900px"
                customHeight="78vh"
                customZIndex={99999}
                contentPadding="0"
                bodyOverflowY="auto"
                mainContent={
                    loadingColumns ? (
                        <div className="filter-loading-state">
                            Učitavanje kolona...
                        </div>
                    ) : (
                        <div className="filter-modal-content-wrap">
                            <div className="filter-rules-tabs">
                                {rules.map((rule, index) => (
                                    <button
                                        key={rule.id}
                                        type="button"
                                        className={`filter-rule-tab ${editingRuleId === rule.id ? "active" : ""
                                            }`}
                                        onClick={() => setEditingRuleId(rule.id)}
                                    >
                                        Uslov {index + 1}
                                    </button>
                                ))}

                                <button
                                    type="button"
                                    className="filter-rule-tab add"
                                    onClick={addRule}
                                >
                                    + Dodaj
                                </button>
                            </div>

                            <div className="filter-modal-scroll">
                                {rules
                                    .filter((rule) =>
                                        editingRuleId ? rule.id === editingRuleId : true
                                    )
                                    .map((rule) => {
                                        const realIndex = rules.findIndex((r) => r.id === rule.id);
                                        const colType = getColumnType(rule.column);
                                        const validOperators = getOperatorsForType(colType);
                                        const visibleOps = OPS.filter((op) =>
                                            validOperators.includes(op.k)
                                        );
                                        const disableValue =
                                            rule.operator === "IS NULL" ||
                                            rule.operator === "IS NOT NULL";
                                        const showSecondValue = rule.operator === "BETWEEN";

                                        return (
                                            <Form
                                                key={rule.id}
                                                className="filter-rule-editor"
                                                onSubmit={(e) => e.preventDefault()}
                                            >
                                                <div className="filter-rule-editor-header">
                                                    <div>
                                                        <div className="filter-rule-editor-title">
                                                            Uređivanje uslova {realIndex + 1}
                                                        </div>

                                                        <div className="filter-rule-editor-subtitle">
                                                            Podesi kolonu, operator i vrijednost
                                                        </div>
                                                    </div>

                                                    {rules.length > 1 && (
                                                        <Button
                                                            type="button"
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => removeRule(rule.id)}
                                                        >
                                                            Ukloni
                                                        </Button>
                                                    )}
                                                </div>

                                                <div className="filter-rule-grid">
                                                    {realIndex > 0 && (
                                                        <Form.Group className="full-row">
                                                            <Form.Label>
                                                                Veza sa prethodnim uslovom
                                                            </Form.Label>

                                                            <Form.Select
                                                                value={rule.joinWithPrev}
                                                                onChange={(e) =>
                                                                    updateRule(rule.id, {
                                                                        joinWithPrev: e.target.value,
                                                                    })
                                                                }
                                                            >
                                                                <option value="AND">AND</option>
                                                                <option value="OR">OR</option>
                                                            </Form.Select>
                                                        </Form.Group>
                                                    )}

                                                    <Form.Group
                                                        controlId={`column-${rule.id}`}
                                                        className="full-row"
                                                    >
                                                        <Form.Label>Kolona</Form.Label>

                                                        <Form.Select
                                                            value={rule.column}
                                                            title={rule.column}
                                                            onChange={(e) =>
                                                                updateRule(rule.id, {
                                                                    column: e.target.value,
                                                                })
                                                            }
                                                        >
                                                            <option value="">Izaberi kolonu</option>

                                                            {columns.map((col) => (
                                                                <option
                                                                    key={col.name}
                                                                    value={col.name}
                                                                    title={`${col.name} (${col.type})`}
                                                                >
                                                                    {col.name} ({col.type})
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>

                                                    <Form.Group controlId={`operator-${rule.id}`}>
                                                        <Form.Label>Operator</Form.Label>

                                                        <Form.Select
                                                            value={rule.operator}
                                                            onChange={(e) =>
                                                                updateRule(rule.id, {
                                                                    operator: e.target.value,
                                                                })
                                                            }
                                                            disabled={!rule.column}
                                                        >
                                                            <option value="">Izaberi operator</option>

                                                            {visibleOps.map((op) => (
                                                                <option key={op.k} value={op.k}>
                                                                    {op.label}
                                                                </option>
                                                            ))}
                                                        </Form.Select>
                                                    </Form.Group>

                                                    {!disableValue && (
                                                        <Form.Group controlId={`value-${rule.id}`}>
                                                            <Form.Label>
                                                                {rule.operator === "IN"
                                                                    ? "Vrijednosti"
                                                                    : "Vrijednost"}
                                                            </Form.Label>

                                                            {isBoolean(colType) ? (
                                                                <Form.Select
                                                                    value={rule.value}
                                                                    onChange={(e) =>
                                                                        updateRule(rule.id, {
                                                                            value: e.target.value,
                                                                        })
                                                                    }
                                                                    disabled={!rule.column}
                                                                >
                                                                    <option value="">
                                                                        Izaberi vrijednost
                                                                    </option>
                                                                    <option value="true">true</option>
                                                                    <option value="false">false</option>
                                                                </Form.Select>
                                                            ) : (
                                                                <Form.Control
                                                                    type={
                                                                        isDateLike(colType)
                                                                            ? "date"
                                                                            : isNumeric(colType)
                                                                                ? "number"
                                                                                : "text"
                                                                    }
                                                                    value={rule.value}
                                                                    onChange={(e) =>
                                                                        updateRule(rule.id, {
                                                                            value: e.target.value,
                                                                        })
                                                                    }
                                                                    disabled={!rule.column}
                                                                    placeholder={
                                                                        rule.operator === "IN"
                                                                            ? "npr. 1,2,3"
                                                                            : isDateLike(colType)
                                                                                ? "Izaberi datum"
                                                                                : "Unesi vrijednost"
                                                                    }
                                                                />
                                                            )}
                                                        </Form.Group>
                                                    )}

                                                    {showSecondValue && (
                                                        <Form.Group controlId={`value2-${rule.id}`}>
                                                            <Form.Label>Druga vrijednost</Form.Label>

                                                            <Form.Control
                                                                type={
                                                                    isDateLike(colType)
                                                                        ? "date"
                                                                        : isNumeric(colType)
                                                                            ? "number"
                                                                            : "text"
                                                                }
                                                                value={rule.value2}
                                                                onChange={(e) =>
                                                                    updateRule(rule.id, {
                                                                        value2: e.target.value,
                                                                    })
                                                                }
                                                                disabled={!rule.column}
                                                            />
                                                        </Form.Group>
                                                    )}
                                                </div>
                                            </Form>
                                        );
                                    })}

                                <details className="filter-preview-details">
                                    <summary>CQL preview</summary>

                                    <div className="filter-preview-code">
                                        {previewCql || "CQL će se pojaviti ovdje..."}
                                    </div>
                                </details>
                            </div>
                        </div>
                    )
                }
                footerContent={
                    <div className="filter-modal-footer-actions">
                        <button
                            type="button"
                            className="app-btn app-btn-success"
                            onClick={applyFilter}
                            disabled={loadingColumns || !selectedLayer}
                        >
                            Primijeni filter
                        </button>

                        <button
                            type="button"
                            className="app-btn app-btn-secondary"
                            onClick={closeFilterModal}
                        >
                            Zatvori
                        </button>
                    </div>
                }
            />
        </div>
    );
};

export default LayerFilterPanel;